import { generateUuid } from "../../utils/id";
import { parsePassthroughAttributes } from "./attributeUtils";
import type {
  Body,
  BodyUUID,
  ContactExclude,
  ContactPair,
  Sensor,
  SensorUUID,
  UUID,
  Vector2,
  Vector5,
} from "../../types/scene";

type WarnFn = (...args: unknown[]) => void;
type BodyWithGeoms = Pick<Body, "name"> & {
  geoms?: Array<{ name?: string; uuid?: unknown }>;
};
type BodyLookup = Record<string, BodyWithGeoms | undefined>;

export const parseContactExcludes = (
  xmlDoc: Document,
  bodiesMap: BodyLookup,
  warn: WarnFn
): ContactExclude[] => {
  const excludes: ContactExclude[] = [];
  const contactRoot = xmlDoc.querySelector("contact");
  if (!contactRoot) return excludes;

  const resolveBodyUuidByName = (targetName: string): BodyUUID | undefined => {
    for (const [uuid, body] of Object.entries(bodiesMap)) {
      if (!body) continue;
      const parsedName = String(body.name || "");
      if (!parsedName) continue;
      if (parsedName === targetName) return uuid as BodyUUID;

      const aliasMatch = parsedName.match(/\(([^()]+)\)\s*$/);
      if (aliasMatch && aliasMatch[1].trim() === targetName) {
        return uuid as BodyUUID;
      }
    }
    return undefined;
  };

  contactRoot.querySelectorAll("exclude").forEach((exclude: Element) => {
    const body1Name = exclude.getAttribute("body1");
    const body2Name = exclude.getAttribute("body2");
    if (!body1Name || !body2Name) return;

    const body1Uuid = resolveBodyUuidByName(body1Name);
    const body2Uuid = resolveBodyUuidByName(body2Name);

    if (!body1Uuid || !body2Uuid) {
      warn(
        `[MJCFParser] Contact exclude: could not resolve body pair "${body1Name}" / "${body2Name}"`
      );
      return;
    }

    excludes.push({ body1: body1Uuid, body2: body2Uuid });
  });

  return excludes;
};

const parseFiniteNumber = (raw: string | null): number | undefined => {
  if (raw == null || raw.trim() === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const parseVector = <T extends number[]>(raw: string | null, length: number): T | undefined => {
  const values = raw
    ? raw
        .trim()
        .split(/\s+/)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
    : undefined;
  if (!values || values.length < length) return undefined;
  const sliced = values.slice(0, length);
  return sliced.every(Number.isFinite) ? (sliced as T) : undefined;
};

export const parseContactPairs = (
  xmlDoc: Document,
  bodiesMap: BodyLookup,
  warn: WarnFn
): ContactPair[] => {
  const pairs: ContactPair[] = [];
  const contactRoot = xmlDoc.querySelector("contact");
  if (!contactRoot) return pairs;

  const geomNameToUuid = new Map<string, UUID>();
  const duplicateGeomNames = new Set<string>();
  Object.values(bodiesMap || {}).forEach((body) => {
    const geoms = Array.isArray(body?.geoms) ? body.geoms : [];
    geoms.forEach((geom) => {
      const name = typeof geom?.name === "string" ? geom.name.trim() : "";
      const uuid = geom?.uuid ? String(geom.uuid) : "";
      if (!name || !uuid) return;
      if (geomNameToUuid.has(name)) {
        duplicateGeomNames.add(name);
        geomNameToUuid.delete(name);
        return;
      }
      if (!duplicateGeomNames.has(name)) {
        geomNameToUuid.set(name, uuid as UUID);
      }
    });
  });

  contactRoot.querySelectorAll("pair").forEach((pairNode: Element) => {
    const geom1Name = pairNode.getAttribute("geom1");
    const geom2Name = pairNode.getAttribute("geom2");
    if (!geom1Name || !geom2Name) return;

    const geom1 = geomNameToUuid.get(geom1Name);
    const geom2 = geomNameToUuid.get(geom2Name);
    if (!geom1 || !geom2) {
      warn(
        `[MJCFParser] Contact pair: could not resolve geom pair "${geom1Name}" / "${geom2Name}"`
      );
      return;
    }

    pairs.push({
      uuid: generateUuid(),
      name: pairNode.getAttribute("name") || undefined,
      geom1,
      geom2,
      condim: parseFiniteNumber(pairNode.getAttribute("condim")),
      priority: parseFiniteNumber(pairNode.getAttribute("priority")),
      friction: parseVector<Vector5>(pairNode.getAttribute("friction"), 5),
      solref: parseVector<Vector2>(pairNode.getAttribute("solref"), 2),
      solimp: parseVector<Vector5>(pairNode.getAttribute("solimp"), 5),
      margin: parseFiniteNumber(pairNode.getAttribute("margin")),
      gap: parseFiniteNumber(pairNode.getAttribute("gap")),
    });
  });

  return pairs;
};

export const parseSensors = (xmlDoc: Document): Record<SensorUUID, Sensor> => {
  const sensors: Record<SensorUUID, Sensor> = {};
  const sensorRoot = xmlDoc.querySelector("sensor");
  if (!sensorRoot) return sensors;

  Array.from(sensorRoot.children).forEach((node: Element) => {
    const kind = node.tagName.toLowerCase();
    const name = node.getAttribute("name") || `${kind}_${Object.keys(sensors).length}`;
    const uuid = generateUuid() as SensorUUID;
    const attrs = parsePassthroughAttributes(node, ["name"]) || {};
    sensors[uuid] = { uuid, kind, name, attrs };
  });

  return sensors;
};
